import { lockDateKey } from './ob-cache-lock-version.ts';

/**
 * Lock dates as calendar dates, for every reader of `lock_versions`.
 *
 * Encompass saves lock dates as calendar dates (`2026-11-12`). Lock versions
 * written by the OB cache refresh keep the instant OB reported, in UTC
 * (`2026-11-13T05:59:00+00:00`), and their first 10 characters are the next
 * day for an evening lock and for OB's 11:59 PM Central expiration. Readers
 * show both as the Pacific calendar date: the rule `lockDateKey` applies and
 * `insert_lock_version_locked` uses to classify relocks. Stored rows keep
 * their raw values; the material hash still uses the first 10 characters.
 *
 * Shared by the portal (src/services/lockCalendarDates.ts) and edge functions.
 */

export { lockDateKey };

/** The four lock-date fields the database's material normalizer treats as dates. */
export const LOCK_CALENDAR_DATE_KEYS = [
  'rate_lock_buy_side_lock_date',
  'rate_lock_buy_side_lock_expires',
  'rate_lock_sell_side_lock_date',
  'rate_lock_sell_side_lock_expires',
] as const;

/** A zoned instant's Pacific date. Calendar dates and unreadable values come back unchanged. */
export function lockCalendarDate<T>(value: T): T | string {
  return lockDateKey(value) ?? value;
}

/** A copy of the fields with the four lock dates as calendar dates. Other keys are untouched. */
export function withLockCalendarDates<T extends Record<string, unknown>>(fields: T): T {
  const mapped: Record<string, unknown> = { ...fields };
  for (const key of LOCK_CALENDAR_DATE_KEYS) {
    if (key in mapped) mapped[key] = lockCalendarDate(mapped[key]);
  }
  return mapped as T;
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

// MM/DD/YYYY from YYYY-MM-DD by position, as the SQL label builder does.
function labelDate(isoDate: string): string {
  return `${isoDate.slice(5, 7)}/${isoDate.slice(8, 10)}/${isoDate.slice(0, 4)}`;
}

/**
 * The stored label with its date as the Pacific calendar date.
 *
 * Until 2026-10-28, `insert_lock_version_locked` built "Lock #N (MM/DD/YYYY)"
 * from the first 10 characters of the lock date, so an OB copy of an evening
 * lock was labeled with the next day. Only that exact label is rewritten;
 * any other label, and any label whose date doesn't change, passes through.
 */
export function lockCalendarLabel(
  label: string | null | undefined,
  version: number | string | null | undefined,
  rawLockDate: unknown,
): string | null {
  if (typeof label !== 'string') return label ?? null;
  if (version === null || version === undefined) return label;
  if (typeof rawLockDate !== 'string' || !ISO_DATE_PREFIX.test(rawLockDate)) return label;
  const prefix = rawLockDate.slice(0, 10);
  const calendar = lockDateKey(rawLockDate);
  if (!calendar || calendar === prefix) return label;
  return label === `Lock #${version} (${labelDate(prefix)})`
    ? `Lock #${version} (${labelDate(calendar)})`
    : label;
}
